import React, { useEffect, useMemo, useState } from "react";
import { api, UGX } from "../lib/api";
import { PageHeader, KpiCard, SectionCard, StatusPill, EmptyState, Chip } from "../components/Common";
import { Pill, AlertTriangle, PackageOpen, CheckCircle2, Repeat, ShieldAlert, Boxes, X, Plus, Receipt, Trash2 } from "lucide-react";
import { toast } from "sonner";

// Best-match catalog price lookup — exact name / brand → generic prefix → 0
const catalogPriceFor = (catalog, drugName) => {
  const q = (drugName || "").trim().toLowerCase();
  if (!q || !catalog) return 0;
  for (const c of catalog) {
    if ((c.name || "").toLowerCase() === q) return Number(c.unit_price) || 0;
    if ((c.brands || []).some((b) => b.toLowerCase() === q)) return Number(c.unit_price) || 0;
  }
  for (const c of catalog) {
    if (q.startsWith(c.generic || "")) return Number(c.unit_price) || 0;
  }
  return 0;
};

export default function Pharmacy() {
  const [rx, setRx] = useState([]);
  const [tab, setTab] = useState("queue"); // queue | dispensed | batches | catalog
  const [batchFilter, setBatchFilter] = useState("all"); // all | expiring
  const [batches, setBatches] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [allergyRx, setAllergyRx] = useState(null); // { rx, hits, pendingLineItems }
  const [subRx, setSubRx] = useState(null); // { rx, index, item }
  const [dispenseRx, setDispenseRx] = useState(null); // rx being dispensed
  const [showAdd, setShowAdd] = useState(false);
  const [editDrug, setEditDrug] = useState(null); // {} for new, {...existing} for edit

  const load = async () => {
    const [{ data: r }, { data: b }, { data: c }] = await Promise.all([
      api.get("/prescriptions"),
      api.get("/pharmacy/batches"),
      api.get("/pharmacy/catalog"),
    ]);
    setRx(r || []); setBatches(b || []); setCatalog(c || []);
  };
  useEffect(() => { load(); }, []);

  const today = new Date().toISOString().slice(0, 10);
  const pending = rx.filter((r) => r.status === "Pending" || r.status === "Partially Dispensed");
  const allDispensed = rx.filter((r) => r.status === "Dispensed");
  const dispensedToday = allDispensed.filter((r) => (r.dispensed_at || "").startsWith(today));
  const activeBatches = batches.filter((b) => Number(b.qty) > 0);
  const soonExpire = batches.filter((b) => {
    if (Number(b.qty) <= 0) return false;
    const days = (new Date(b.expiry_date) - new Date()) / 86400000;
    return days <= 60;
  });

  const submitDispense = async (rxObj, lineItems, totalOverride, override = false) => {
    try {
      const { data } = await api.post(
        `/prescriptions/${rxObj.rx_id}/dispense${override ? "?override=true" : ""}`,
        { line_items: lineItems, total_override: totalOverride }
      );
      const inv = data?.invoice;
      const partial = data?.status === "Partially Dispensed";
      const msg = inv
        ? `${partial ? "Partial dispense" : "Dispensed"} · billed ${UGX(inv.total)} — Invoice ${inv.invoice_no}`
        : (partial ? "Partial dispense recorded" : "Dispensed · stock updated");
      toast.success(msg);
      setDispenseRx(null);
      setAllergyRx(null);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.error === "allergy_block") {
        setAllergyRx({ rx: rxObj, hits: detail.hits, pendingLineItems: lineItems, pendingTotal: totalOverride });
      } else {
        toast.error(typeof detail === "string" ? detail : "Failed to dispense");
      }
    }
  };

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="pharmacy-page">
      <PageHeader title="Pharmacy" subtitle="Review prescriptions, check allergies, substitute brands and dispense with FEFO batches." />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard testId="k-pending"     onClick={() => { setTab("queue"); }}                                        label="Pending prescriptions" value={pending.length}         icon={Pill}          tone="amber" hint="Click to review queue" />
        <KpiCard testId="k-dispensed"   onClick={() => { setTab("dispensed"); }}                                    label="Dispensed today"       value={dispensedToday.length}  icon={CheckCircle2}  tone="green" hint="Click for today's list" />
        <KpiCard testId="k-batches"     onClick={() => { setBatchFilter("all"); setTab("batches"); }}               label="Batches on shelf"      value={activeBatches.length}   icon={Boxes}         tone="blue"  hint="Click for FEFO board" />
        <KpiCard testId="k-expiring"    onClick={() => { setBatchFilter("expiring"); setTab("batches"); }}          label="Expiring < 60d"        value={soonExpire.length}      icon={AlertTriangle} tone="red"   hint="Click to act now" />
      </div>

      <div className="border-b border-slate-200 flex gap-1">
        {[["queue","Prescription queue"],["dispensed","Dispensed today"],["batches","Batches & FEFO"],["catalog","Drug catalog"]].map(([k,l])=>(
          <button key={k} data-testid={`pharm-tab-${k}`} onClick={()=>setTab(k)} className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px ${tab===k?"border-[#1976D2] text-[#1565C0]":"border-transparent text-slate-500 hover:text-slate-800"}`}>{l}</button>
        ))}
      </div>

      {tab === "queue" && (
        <SectionCard>
          {pending.length === 0 ? <EmptyState icon={Pill} title="No pending prescriptions" /> : (
            <ul className="divide-y divide-slate-100">
              {pending.map((r) => (
                <li key={r.rx_id} className="p-4" data-testid={`rx-${r.rx_id}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-semibold">{r.rx_no}</div>
                      <div className="text-xs text-slate-500">{r.patient_name} · {r.patient_ths_id} · {new Date(r.created_at).toLocaleString()}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <StatusPill status={r.status} />
                      <button data-testid={`dispense-${r.rx_id}`} onClick={() => setDispenseRx(r)} className="px-3 h-8 rounded-lg bg-[#1976D2] hover:bg-[#1565C0] text-white text-xs font-semibold">Dispense</button>
                    </div>
                  </div>
                  <ul className="mt-2 space-y-1">
                    {(r.items || []).map((i, idx) => {
                      const prescribed = Number(i.qty) || 0;
                      const already = Number(i.dispensed_qty) || 0;
                      const remaining = Math.max(0, prescribed - already);
                      return (
                        <li key={`${r.rx_id}-${i.drug}-${idx}`} className="text-sm text-slate-700 flex items-center gap-2">
                          <span className="flex-1">
                            {i.drug} — {i.dose} {i.frequency} × {i.duration} (Qty {prescribed})
                            {already > 0 && <span className="ml-2 chip chip-amber text-[10px]" data-testid={`rx-remaining-${r.rx_id}-${idx}`}>Dispensed {already} · {remaining} remaining</span>}
                            {i.substituted_from && <span className="ml-2 chip chip-blue text-[10px]">sub from {i.substituted_from}</span>}
                          </span>
                          <button data-testid={`sub-${r.rx_id}-${idx}`} onClick={() => setSubRx({ rx: r, index: idx, item: i })} className="text-xs text-[#1565C0] font-medium hover:underline flex items-center gap-1"><Repeat className="w-3 h-3" /> Substitute</button>
                        </li>
                      );
                    })}
                  </ul>
                </li>
              ))}
            </ul>
          )}
        </SectionCard>
      )}

      {tab === "dispensed" && (
        <SectionCard
          title={`Dispensed today · ${dispensedToday.length}`}
          testId="dispensed-today-card"
          actions={<span className="text-xs text-slate-500 tabular">{today}</span>}
        >
          {dispensedToday.length === 0 ? <EmptyState icon={CheckCircle2} title="Nothing dispensed today yet" description="As pharmacists confirm dispenses, they'll appear here." /> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="dispensed-today-table">
                <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="text-left px-4 py-2.5">Time</th>
                    <th className="text-left px-4 py-2.5">Rx</th>
                    <th className="text-left px-4 py-2.5">Patient</th>
                    <th className="text-left px-4 py-2.5">Drug(s) · Qty</th>
                    <th className="text-left px-4 py-2.5">Dispensed by</th>
                    <th className="text-left px-4 py-2.5">Notes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {dispensedToday.map((r) => (
                    <tr key={r.rx_id} data-testid={`dt-${r.rx_id}`}>
                      <td className="px-4 py-2.5 tabular text-xs">{r.dispensed_at ? new Date(r.dispensed_at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }) : "—"}</td>
                      <td className="px-4 py-2.5 tabular text-xs">{r.rx_no}</td>
                      <td className="px-4 py-2.5"><div className="font-medium">{r.patient_name}</div><div className="text-[11px] text-slate-500 tabular">{r.patient_ths_id}</div></td>
                      <td className="px-4 py-2.5">
                        <ul className="text-xs space-y-0.5">
                          {(r.items || []).map((i, idx) => (
                            <li key={`${r.rx_id}-${i.drug}-${idx}`}>{i.drug} <span className="tabular text-slate-500">× {i.qty}</span></li>
                          ))}
                        </ul>
                      </td>
                      <td className="px-4 py-2.5 text-xs">{r.dispensed_by_name || "—"}{r.allergy_override && <div className="chip chip-amber text-[10px] mt-0.5">Allergy override</div>}</td>
                      <td className="px-4 py-2.5 text-xs text-slate-500">{r.notes || "—"}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-slate-50">
                  <tr><td colSpan={6} className="px-4 py-2 text-right text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Total dispensed today: <span className="tabular text-slate-900">{dispensedToday.length}</span></td></tr>
                </tfoot>
              </table>
            </div>
          )}
        </SectionCard>
      )}

      {tab === "batches" && (
        <SectionCard
          title={batchFilter === "expiring" ? `Expiring soon · < 60 days · ${soonExpire.length}` : `Batches on shelf · FEFO order (earliest expiry first) · ${activeBatches.length} active${batches.length - activeBatches.length ? ` · ${batches.length - activeBatches.length} empty` : ""}`}
          actions={
            <div className="flex items-center gap-2">
              {batchFilter === "expiring" && (
                <button data-testid="batches-clear-filter" onClick={() => setBatchFilter("all")} className="h-8 px-3 rounded-md border border-slate-200 hover:bg-slate-50 text-xs font-medium flex items-center gap-1">
                  <X className="w-3 h-3" /> Clear filter (show all {batches.length})
                </button>
              )}
              <button data-testid="add-inventory-btn" onClick={() => setShowAdd(true)} className="h-9 px-3 rounded-lg bg-[#2E7D32] hover:bg-[#1B5E20] text-white text-xs font-semibold flex items-center gap-1.5"><Plus className="w-3.5 h-3.5" /> Add inventory</button>
            </div>
          }
        >
          {(() => {
            const rows = batchFilter === "expiring" ? soonExpire : batches;
            if (rows.length === 0) return <EmptyState icon={Boxes} title={batchFilter === "expiring" ? "Nothing expiring in the next 60 days" : "No batches on file"} />;
            return (
              <table className="w-full text-sm" data-testid="batches-table">
                <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                  <tr><th className="text-left px-4 py-2.5">Drug</th><th className="text-left px-4 py-2.5">Lot no.</th><th className="text-left px-4 py-2.5">Expiry</th><th className="text-right px-4 py-2.5">Qty</th><th className="text-left px-4 py-2.5">Alert</th></tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.map((b) => {
                    const days = Math.round((new Date(b.expiry_date) - new Date()) / 86400000);
                    const empty = Number(b.qty) <= 0;
                    const bad = !empty && days < 30;
                    const warn = !empty && days < 60;
                    return (
                      <tr key={b.batch_id} data-testid={`batch-${b.batch_id}`} className={empty ? "bg-slate-50 text-slate-400" : bad ? "bg-red-50/60" : warn ? "bg-amber-50/60" : ""}>
                        <td className="px-4 py-2.5 font-medium">{b.drug}</td>
                        <td className="px-4 py-2.5 tabular">{b.lot_no}</td>
                        <td className="px-4 py-2.5 tabular">{b.expiry_date} <span className="text-xs text-slate-500">({days}d)</span></td>
                        <td className="px-4 py-2.5 text-right tabular font-semibold">{b.qty}</td>
                        <td className="px-4 py-2.5">{empty ? <Chip tone="slate">Empty</Chip> : bad ? <Chip tone="red">Recall / discard</Chip> : warn ? <Chip tone="amber">Use first</Chip> : <Chip tone="slate">OK</Chip>}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="bg-slate-50">
                  <tr><td colSpan={5} className="px-4 py-2 text-right text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{batchFilter === "expiring" ? `Expiring within 60d: ${soonExpire.length} of ${activeBatches.length} active` : `Batches on shelf: ${activeBatches.length} active · ${batches.length - activeBatches.length} empty`}</td></tr>
                </tfoot>
              </table>
            );
          })()}
        </SectionCard>
      )}

      {tab === "catalog" && (
        <SectionCard
          title="Drug catalog · generics ⇄ brands"
          actions={<button data-testid="add-drug-btn" onClick={() => setEditDrug({})} className="h-9 px-3 rounded-lg bg-[#2E7D32] hover:bg-[#1B5E20] text-white text-xs font-semibold flex items-center gap-1.5"><Plus className="w-3.5 h-3.5" /> Add drug</button>}
        >
          <table className="w-full text-sm" data-testid="catalog-table">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr><th className="text-left px-4 py-2.5">Generic</th><th className="text-left px-4 py-2.5">Preferred</th><th className="text-left px-4 py-2.5">Brand equivalents</th><th className="text-left px-4 py-2.5">Class</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {catalog.map((c) => (
                <tr key={c.generic} data-testid={`catalog-row-${c.generic}`} onClick={() => setEditDrug(c)} className="cursor-pointer hover:bg-slate-50">
                  <td className="px-4 py-2.5 capitalize font-medium">{c.generic}</td>
                  <td className="px-4 py-2.5">{c.name}</td>
                  <td className="px-4 py-2.5 text-slate-600">{(c.brands || []).join(" · ") || <span className="italic text-slate-400">none</span>}</td>
                  <td className="px-4 py-2.5"><Chip tone="slate">{c.class}</Chip></td>
                </tr>
              ))}
            </tbody>
          </table>
        </SectionCard>
      )}

      <SectionCard title="Recently dispensed">
        {allDispensed.length === 0 ? <EmptyState icon={CheckCircle2} title="Nothing dispensed yet" /> : (
          <ul className="divide-y divide-slate-100">
            {allDispensed.slice(0, 6).map((r) => (
              <li key={r.rx_id} className="p-4 flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium">{r.rx_no}</div>
                  <div className="text-xs text-slate-500">{r.patient_name} · Dispensed by {r.dispensed_by_name}{r.allergy_override && <span className="ml-2 chip chip-amber text-[10px]">Allergy override</span>}</div>
                </div>
                <StatusPill status={r.status} />
              </li>
            ))}
          </ul>
        )}
      </SectionCard>

      {allergyRx && (
        <div className="fixed inset-0 z-40 bg-slate-900/50 flex items-center justify-center p-4" onClick={() => setAllergyRx(null)}>
          <div className="bg-white rounded-2xl border border-slate-200 max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="allergy-modal">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-[#fdecec] text-[#B71C1C] flex items-center justify-center flex-shrink-0"><ShieldAlert className="w-5 h-5" /></div>
              <div className="flex-1">
                <div className="font-semibold text-[#B71C1C]">Allergy conflict detected</div>
                <div className="text-sm text-slate-600 mt-1">Patient {allergyRx.rx.patient_name} is allergic to:</div>
                <ul className="mt-2 space-y-1">
                  {allergyRx.hits.map((h, i) => (
                    <li key={i} className="text-sm rounded bg-[#fdecec] border border-[#f5c2c2] px-2.5 py-1.5"><strong>{h.allergy}</strong> — will react with <strong>{h.drug}</strong></li>
                  ))}
                </ul>
                <div className="text-xs text-slate-500 mt-3">Dispensing this prescription requires clinical override. Consult the prescriber if in doubt.</div>
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setAllergyRx(null)} className="h-10 px-4 rounded-lg border border-slate-300 text-sm font-medium">Cancel</button>
              <button data-testid="allergy-override" onClick={() => submitDispense(allergyRx.rx, allergyRx.pendingLineItems, allergyRx.pendingTotal, true)} className="h-10 px-4 rounded-lg bg-[#B71C1C] hover:bg-[#8f1515] text-white text-sm font-semibold">Override & dispense</button>
            </div>
          </div>
        </div>
      )}

      {dispenseRx && (
        <DispenseModal
          rx={dispenseRx}
          catalog={catalog}
          onClose={() => setDispenseRx(null)}
          onSubmit={(rows, totalOverride) => submitDispense(dispenseRx, rows, totalOverride, false)}
        />
      )}

      {subRx && (
        <SubstitutionModal state={subRx} catalog={catalog} onClose={() => setSubRx(null)} onDone={() => { setSubRx(null); load(); }} />
      )}
      {showAdd && (
        <AddInventoryModal catalog={catalog} onClose={() => setShowAdd(false)} onDone={() => { setShowAdd(false); load(); }} />
      )}
      {editDrug && (
        <DrugCatalogModal drug={editDrug} catalog={catalog} onClose={() => setEditDrug(null)} onDone={() => { setEditDrug(null); load(); }} />
      )}
    </div>
  );
}

function DrugCatalogModal({ drug, catalog, onClose, onDone }) {
  const isEdit = !!drug.generic;
  const [generic, setGeneric] = useState(drug.generic || "");
  const [name, setName] = useState(drug.name || "");
  const [drugClass, setDrugClass] = useState(drug.class || "");
  const [customClass, setCustomClass] = useState("");
  const [brands, setBrands] = useState(drug.brands && drug.brands.length ? drug.brands : [""]);
  const [saving, setSaving] = useState(false);

  const existingClasses = Array.from(new Set(catalog.map((c) => c.class).filter(Boolean))).sort();

  const updateBrand = (i, v) => setBrands((arr) => arr.map((b, idx) => idx === i ? v : b));
  const addBrand = () => setBrands((arr) => [...arr, ""]);
  const removeBrand = (i) => setBrands((arr) => arr.length === 1 ? [""] : arr.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (!generic.trim() || !name.trim()) { toast.error("Generic name and Preferred name are required"); return; }
    const finalClass = drugClass === "__custom__" ? customClass.trim() : drugClass;
    if (!finalClass) { toast.error("Class is required"); return; }
    const cleanBrands = brands.map((b) => b.trim()).filter(Boolean);
    setSaving(true);
    try {
      const payload = { generic: generic.trim().toLowerCase(), name: name.trim(), brands: cleanBrands, drug_class: finalClass };
      if (isEdit) {
        await api.put(`/pharmacy/catalog/${encodeURIComponent(drug.generic)}`, payload);
        toast.success(`${name} updated`);
      } else {
        await api.post("/pharmacy/catalog", payload);
        toast.success(`${name} added to catalog`);
      }
      onDone();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.error === "duplicate") {
        toast.error(detail.message || "Drug already exists — clicking the existing row lets you edit it.");
      } else {
        toast.error(typeof detail === "string" ? detail : "Failed to save");
      }
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-40 bg-slate-900/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl border border-slate-200 max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="drug-catalog-modal">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-lg font-semibold">{isEdit ? "Edit drug" : "Add drug to catalog"}</div>
            <div className="text-xs text-slate-500">{isEdit ? "Update brand equivalents or class." : "Register a new generic + its brand alternatives."}</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
        </div>

        <div className="space-y-3">
          <label className="block">
            <span className="text-[11px] uppercase text-slate-500 font-semibold">Generic name *</span>
            <input data-testid="drug-generic" value={generic} disabled={isEdit} onChange={(e) => setGeneric(e.target.value)} placeholder="e.g. paracetamol" className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm disabled:bg-slate-50 disabled:text-slate-500 focus:outline-none focus:ring-2 focus:ring-[#1976D2]/40" />
          </label>
          <label className="block">
            <span className="text-[11px] uppercase text-slate-500 font-semibold">Preferred (generic + strength) *</span>
            <input data-testid="drug-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Paracetamol 500mg" className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1976D2]/40" />
          </label>
          <label className="block">
            <span className="text-[11px] uppercase text-slate-500 font-semibold">Class *</span>
            <select data-testid="drug-class" value={drugClass} onChange={(e) => setDrugClass(e.target.value)} className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#1976D2]/40">
              <option value="">— Select class —</option>
              {existingClasses.map((c) => <option key={c} value={c}>{c}</option>)}
              <option value="__custom__">+ Add new class…</option>
            </select>
            {drugClass === "__custom__" && (
              <input data-testid="drug-class-custom" value={customClass} onChange={(e) => setCustomClass(e.target.value)} placeholder="New class name" className="mt-2 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1976D2]/40" />
            )}
          </label>

          <div>
            <span className="text-[11px] uppercase text-slate-500 font-semibold">Brand equivalents</span>
            <div className="mt-1 space-y-2">
              {brands.map((b, i) => (
                <div key={i} className="flex gap-2">
                  <input data-testid={`drug-brand-${i}`} value={b} onChange={(e) => updateBrand(i, e.target.value)} placeholder={i === 0 ? "e.g. Panadol 500mg" : "e.g. Fever-P 500mg"} className="flex-1 h-10 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1976D2]/40" />
                  <button data-testid={`drug-brand-remove-${i}`} onClick={() => removeBrand(i)} className="text-slate-400 hover:text-red-600 px-2"><X className="w-4 h-4" /></button>
                </div>
              ))}
              <button data-testid="drug-brand-add" onClick={addBrand} className="text-xs text-[#1565C0] font-medium flex items-center gap-1 hover:underline"><Plus className="w-3 h-3" /> Add another brand</button>
            </div>
          </div>
        </div>

        <div className="mt-5 flex justify-between gap-2">
          {isEdit ? (
            <button
              data-testid="drug-delete"
              onClick={async () => {
                if (!window.confirm(`Delete "${drug.name}" from the catalog?\n\nThis cannot be undone. Existing batches on shelf and past prescriptions are preserved — only the reference entry is removed.`)) return;
                setSaving(true);
                try {
                  const { data } = await api.delete(`/pharmacy/catalog/${encodeURIComponent(drug.generic)}`);
                  toast.success(`${drug.name} removed${data.open_batches ? ` (note: ${data.open_batches} open batch${data.open_batches === 1 ? "" : "es"} still on shelf)` : ""}`);
                  onDone();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Failed to delete");
                } finally { setSaving(false); }
              }}
              disabled={saving}
              className="h-10 px-3 rounded-lg border border-red-200 text-red-700 hover:bg-red-50 text-sm font-medium disabled:opacity-50 flex items-center gap-1.5"
            >
              <Trash2 className="w-3.5 h-3.5" /> Delete
            </button>
          ) : <span />}
          <div className="flex gap-2">
            <button onClick={onClose} className="h-10 px-4 rounded-lg border border-slate-300 text-sm font-medium">Cancel</button>
            <button data-testid="drug-submit" onClick={submit} disabled={saving} className="h-10 px-5 rounded-lg bg-[#2E7D32] hover:bg-[#1B5E20] text-white text-sm font-semibold disabled:opacity-50">
              {saving ? "Saving…" : isEdit ? "Save changes" : "Add drug"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function AddInventoryModal({ catalog, onClose, onDone }) {
  const [drug, setDrug] = useState("");
  const [lotNo, setLotNo] = useState("");
  const [expiry, setExpiry] = useState("");
  const [qty, setQty] = useState(100);
  const [drugClass, setDrugClass] = useState("");
  const [unitCost, setUnitCost] = useState(0);
  const [saving, setSaving] = useState(false);

  const CLASSES = ["Analgesic","NSAID","Antibiotic","Antimalarial","Antihypertensive","Antidiabetic","CCB","ACEi","Antihistamine","Antipyretic","Antiplatelet","Supplement","Other"];

  // Auto-fill drug class if user picks a known catalog drug
  useEffect(() => {
    const match = catalog.find((c) => c.name.toLowerCase() === drug.trim().toLowerCase() || (c.brands || []).some((b) => b.toLowerCase() === drug.trim().toLowerCase()));
    if (match && !drugClass) setDrugClass(match.class);
  }, [drug]); // eslint-disable-line

  const submit = async () => {
    if (!drug.trim() || !lotNo.trim() || !expiry || !(Number(qty) > 0)) {
      toast.error("Drug name, lot no., expiry and a positive quantity are required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/pharmacy/batches", {
        drug: drug.trim(),
        lot_no: lotNo.trim(),
        expiry_date: expiry,
        qty: Number(qty),
        unit_cost: Number(unitCost) || 0,
        drug_class: drugClass || null,
      });
      toast.success(`${qty} units of ${drug} added · Lot ${lotNo}`);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add inventory");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-40 bg-slate-900/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl border border-slate-200 max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="add-inventory-modal">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-lg font-semibold">Add inventory</div>
            <div className="text-xs text-slate-500">Register a new batch of medication received into the pharmacy.</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
        </div>

        <div className="space-y-3">
          <label className="block">
            <span className="text-[11px] uppercase text-slate-500 font-semibold">Drug name *</span>
            <input data-testid="inv-drug" list="drug-catalog-list" value={drug} onChange={(e) => setDrug(e.target.value)} placeholder="e.g. Paracetamol 500mg" className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1976D2]/40" />
            <datalist id="drug-catalog-list">
              {catalog.flatMap((c) => [c.name, ...(c.brands || [])]).map((n) => <option key={n} value={n} />)}
            </datalist>
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[11px] uppercase text-slate-500 font-semibold">Lot number *</span>
              <input data-testid="inv-lot" value={lotNo} onChange={(e) => setLotNo(e.target.value)} placeholder="e.g. PC-2401-03" className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm tabular focus:outline-none focus:ring-2 focus:ring-[#1976D2]/40" />
            </label>
            <label className="block">
              <span className="text-[11px] uppercase text-slate-500 font-semibold">Expiry date *</span>
              <input data-testid="inv-expiry" type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)} className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm tabular focus:outline-none focus:ring-2 focus:ring-[#1976D2]/40" />
            </label>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[11px] uppercase text-slate-500 font-semibold">Quantity *</span>
              <input data-testid="inv-qty" type="number" min="1" value={qty} onChange={(e) => setQty(e.target.value)} className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm text-right tabular focus:outline-none focus:ring-2 focus:ring-[#1976D2]/40" />
            </label>
            <label className="block">
              <span className="text-[11px] uppercase text-slate-500 font-semibold">Drug class</span>
              <select data-testid="inv-class" value={drugClass} onChange={(e) => setDrugClass(e.target.value)} className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#1976D2]/40">
                <option value="">— Select —</option>
                {CLASSES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
          </div>

          <label className="block">
            <span className="text-[11px] uppercase text-slate-500 font-semibold">Unit cost (UGX) · optional</span>
            <input data-testid="inv-cost" type="number" min="0" value={unitCost} onChange={(e) => setUnitCost(e.target.value)} className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm text-right tabular focus:outline-none focus:ring-2 focus:ring-[#1976D2]/40" />
          </label>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="h-10 px-4 rounded-lg border border-slate-300 text-sm font-medium">Cancel</button>
          <button data-testid="inv-submit" onClick={submit} disabled={saving} className="h-10 px-5 rounded-lg bg-[#2E7D32] hover:bg-[#1B5E20] text-white text-sm font-semibold disabled:opacity-50">
            {saving ? "Adding…" : "Add to inventory"}
          </button>
        </div>
      </div>
    </div>
  );
}

function SubstitutionModal({ state, catalog, onClose, onDone }) {
  const [newDrug, setNewDrug] = useState("");
  const [reason, setReason] = useState("Out of stock");
  const original = state.item.drug.toLowerCase();
  const suggestions = catalog.filter((c) => original.includes(c.generic) || c.name.toLowerCase() === original || (c.brands || []).some((b) => original.includes(b.toLowerCase())));
  const options = suggestions.flatMap((c) => [c.name, ...(c.brands || [])]).filter((n) => n.toLowerCase() !== state.item.drug.toLowerCase());

  const submit = async () => {
    if (!newDrug) return toast.error("Choose a substitute");
    try {
      await api.post("/pharmacy/substitute", { rx_id: state.rx.rx_id, item_index: state.index, new_drug: newDrug, reason });
      toast.success(`Substituted → ${newDrug}`);
      onDone();
    } catch { toast.error("Substitution failed"); }
  };
  return (
    <div className="fixed inset-0 z-40 bg-slate-900/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl border border-slate-200 max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="sub-modal">
        <div className="flex items-center justify-between mb-3">
          <div className="font-semibold">Substitute drug</div>
          <button onClick={onClose} className="text-slate-400"><X className="w-4 h-4" /></button>
        </div>
        <div className="text-xs text-slate-500">Original</div>
        <div className="font-medium mb-3">{state.item.drug}</div>
        <label className="block mb-3">
          <span className="text-[11px] uppercase text-slate-500 font-semibold">Substitute with</span>
          {options.length ? (
            <select data-testid="sub-choice" value={newDrug} onChange={(e) => setNewDrug(e.target.value)} className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white">
              <option value="">Choose…</option>
              {options.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          ) : (
            <input data-testid="sub-freeform" value={newDrug} onChange={(e) => setNewDrug(e.target.value)} placeholder="Enter drug name" className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
          )}
        </label>
        <label className="block mb-3">
          <span className="text-[11px] uppercase text-slate-500 font-semibold">Reason</span>
          <select data-testid="sub-reason" value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white">
            <option>Out of stock</option><option>Brand preferred by patient</option><option>Insurance formulary</option><option>Cost</option><option>Prescriber requested change</option>
          </select>
        </label>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="h-10 px-4 rounded-lg border border-slate-300 text-sm font-medium">Cancel</button>
          <button data-testid="sub-submit" onClick={submit} className="h-10 px-4 rounded-lg bg-[#1976D2] hover:bg-[#1565C0] text-white text-sm font-semibold">Confirm substitution</button>
        </div>
      </div>
    </div>
  );
}


function DispenseModal({ rx, catalog, onClose, onSubmit }) {
  const rows0 = useMemo(() => (
    (rx.items || []).map((it, idx) => {
      const prescribed = Number(it.qty) || 0;
      const already = Number(it.dispensed_qty) || 0;
      const remaining = Math.max(0, prescribed - already);
      const unit_price = catalogPriceFor(catalog, it.drug);
      return {
        index: idx,
        drug: it.drug,
        dose: it.dose,
        frequency: it.frequency,
        duration: it.duration,
        prescribed,
        already,
        remaining,
        qty: remaining,
        unit_price,
        substituted_from: it.substituted_from || null,
      };
    })
  ), [rx, catalog]);

  const [rows, setRows] = useState(rows0);
  const [totalMode, setTotalMode] = useState("auto");
  const [customTotal, setCustomTotal] = useState(0);

  const computedTotal = rows.reduce((s, r) => s + (Number(r.qty) || 0) * (Number(r.unit_price) || 0), 0);
  const effectiveTotal = totalMode === "override" ? Math.max(0, Number(customTotal) || 0) : computedTotal;

  const dispensableRows = rows.filter((r) => r.remaining > 0);
  const dispenseNowRows = rows.filter((r) => (Number(r.qty) || 0) > 0);
  const willBePartial = dispenseNowRows.some((r) => (Number(r.qty) || 0) < r.remaining) || dispensableRows.some((r) => !dispenseNowRows.find((x) => x.index === r.index));
  const canSubmit = dispenseNowRows.length > 0;

  const setQty = (i, v) => {
    const n = Math.max(0, Number(v) || 0);
    setRows((rs) => rs.map((r, idx) => idx === i ? { ...r, qty: Math.min(n, r.remaining) } : r));
  };
  const setPrice = (i, v) => {
    const n = Math.max(0, Number(v) || 0);
    setRows((rs) => rs.map((r, idx) => idx === i ? { ...r, unit_price: n } : r));
  };

  const submit = () => {
    if (!canSubmit) { toast.error("Set a quantity to dispense for at least one drug"); return; }
    if (effectiveTotal <= 0) {
      const ok = window.confirm("Billing total is UGX 0 (free / charity dispense) — continue?");
      if (!ok) return;
    }
    const line_items = dispenseNowRows.map((r) => ({
      index: r.index,
      drug: r.drug,
      qty: Number(r.qty),
      unit_price: Number(r.unit_price) || 0,
    }));
    const totalOverride = totalMode === "override" ? Number(effectiveTotal) : null;
    onSubmit(line_items, totalOverride);
  };

  return (
    <div className="fixed inset-0 z-40 bg-slate-900/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl border border-slate-200 max-w-3xl w-full max-h-[92vh] flex flex-col shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="dispense-modal">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-lg font-semibold">Dispense & bill · {rx.rx_no}</div>
            <div className="text-xs text-slate-500">{rx.patient_name} · {rx.patient_ths_id}</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <table className="w-full text-sm border border-slate-200 rounded-lg overflow-hidden" data-testid="dispense-rows">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-3 py-2">Drug</th>
                <th className="text-right px-3 py-2">Prescribed</th>
                <th className="text-right px-3 py-2">Remaining</th>
                <th className="text-right px-3 py-2 w-24">Dispense now</th>
                <th className="text-right px-3 py-2 w-32">Unit price</th>
                <th className="text-right px-3 py-2">Subtotal</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r, i) => {
                const sub = (Number(r.qty) || 0) * (Number(r.unit_price) || 0);
                const isFulfilled = r.remaining === 0;
                return (
                  <tr key={r.index} data-testid={`dispense-row-${r.index}`} className={isFulfilled ? "opacity-60 bg-slate-50" : ""}>
                    <td className="px-3 py-2">
                      <div className="font-medium">{r.drug}</div>
                      <div className="text-[11px] text-slate-500">{r.dose} {r.frequency} × {r.duration}{r.substituted_from ? ` · substituted from ${r.substituted_from}` : ""}</div>
                    </td>
                    <td className="px-3 py-2 text-right tabular text-xs">{r.prescribed}</td>
                    <td className="px-3 py-2 text-right tabular text-xs">
                      {r.remaining}
                      {r.already > 0 && <div className="text-[10px] text-slate-500">({r.already} done)</div>}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <input data-testid={`dispense-qty-${r.index}`} type="number" min={0} max={r.remaining} disabled={isFulfilled} value={r.qty} onChange={(e) => setQty(i, e.target.value)} className="w-20 h-8 px-2 rounded-md border border-slate-200 text-sm text-right tabular focus:outline-none focus:ring-2 focus:ring-[#1976D2]/40 disabled:bg-slate-100" />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <input data-testid={`dispense-price-${r.index}`} type="number" min={0} step={50} disabled={isFulfilled} value={r.unit_price} onChange={(e) => setPrice(i, e.target.value)} className="w-28 h-8 px-2 rounded-md border border-slate-200 text-sm text-right tabular focus:outline-none focus:ring-2 focus:ring-[#1976D2]/40 disabled:bg-slate-100" />
                      {catalogPriceFor(catalog, r.drug) === 0 && !isFulfilled && (
                        <div className="text-[10px] text-amber-600 mt-0.5">No catalog price · set manually</div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right tabular font-semibold">{UGX(sub)}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="bg-slate-50">
              <tr>
                <td colSpan={5} className="px-3 py-2 text-right text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Computed total</td>
                <td className="px-3 py-2 text-right tabular font-bold" data-testid="dispense-computed">{UGX(computedTotal)}</td>
              </tr>
            </tfoot>
          </table>

          <div className="rounded-lg border border-[#e3ebf5] bg-[#f6f9ff] p-3">
            <div className="text-[11px] uppercase tracking-wider text-[#1565C0] font-semibold mb-2 flex items-center gap-1.5">
              <Receipt className="w-3.5 h-3.5" /> Billing total
            </div>
            <div className="flex items-center gap-4 flex-wrap">
              <label className="flex items-center gap-1.5 text-sm">
                <input data-testid="dispense-total-auto" type="radio" checked={totalMode === "auto"} onChange={() => setTotalMode("auto")} className="accent-[#1976D2]" />
                Bill as computed <span className="tabular font-semibold">{UGX(computedTotal)}</span>
              </label>
              <label className="flex items-center gap-1.5 text-sm">
                <input data-testid="dispense-total-override" type="radio" checked={totalMode === "override"} onChange={() => { setTotalMode("override"); setCustomTotal(computedTotal); }} className="accent-[#1976D2]" />
                Override amount
              </label>
              {totalMode === "override" && (
                <input data-testid="dispense-custom-total" type="number" min={0} step={100} value={customTotal} onChange={(e) => setCustomTotal(e.target.value)} className="w-32 h-9 px-2 rounded-md border border-slate-200 text-sm text-right tabular focus:outline-none focus:ring-2 focus:ring-[#1976D2]/40" />
              )}
            </div>
            {totalMode === "override" && effectiveTotal !== computedTotal && (
              <div className="text-[11px] text-slate-500 mt-1.5 tabular">
                Adjustment: <span className={effectiveTotal < computedTotal ? "text-emerald-700 font-semibold" : "text-[#B71C1C] font-semibold"}>{effectiveTotal < computedTotal ? "-" : "+"}{UGX(Math.abs(effectiveTotal - computedTotal))}</span> vs computed
              </div>
            )}
          </div>

          {willBePartial && (
            <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900 flex items-start gap-2" data-testid="dispense-partial-flag">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5" />
              <div>Partial dispense — the prescription will remain in the queue with the remainder to be dispensed later. Only what you dispense now will be billed.</div>
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-between gap-2">
          <div className="text-sm text-slate-600">
            Billing <span className="font-bold tabular text-slate-900" data-testid="dispense-effective-total">{UGX(effectiveTotal)}</span>
            <span className="text-xs text-slate-500 ml-2">· charged to patient's Billing account</span>
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="h-10 px-4 rounded-lg border border-slate-300 text-sm font-medium">Cancel</button>
            <button data-testid="dispense-confirm" disabled={!canSubmit} onClick={submit} className="h-10 px-5 rounded-lg bg-[#2E7D32] hover:bg-[#1B5E20] text-white text-sm font-semibold disabled:opacity-50 flex items-center gap-1.5">
              <CheckCircle2 className="w-4 h-4" /> Confirm dispense & bill
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
